import React from 'react';
import AvTable from '@components/Table/AvTable';
import { ChartClickInteraction } from '@components/Widgets/types';
import { HeadCellType, SortType } from '@fabric-ui/components/Table';
import { flex } from '@fabric-ui/theme';
import { emptyArray, emptyObject } from '@fabric-ui/utils';
import { Box, SxProps, useTheme } from '@mui/material';
import { getSortingPath } from '../../../EditCustomDashboard/Tabs/utils';
import { PivotTableRequest, SortBy, SortDir, TableRowSize } from '../../../types';
import { FieldStyleType } from '../../../types/types';
import { commonTableProps } from './constants';
import { COLUMNS_HEADERS_STYLE_CLASS, EXISTING_COULMN_HEADERS_OVERRIDE_STYLE_CLASS } from './PivotTable/constants';
import useGetPivotTableProps from './PivotTable/useGetPivotTableProps';
import { getSortingDir } from './PivotTable/utils';

interface Props {
  widgetData: PivotTableRequest;
  groupOthers?: boolean;
  offset?: number;
  rowSize: TableRowSize;
  showTotals?: boolean;
  showColTotals: boolean;
  data: any;
  headCells: HeadCellType[];
  onSort: (newSorting: SortBy[]) => void;
  isLoading: boolean;
  rowLimit: number;
  onPaginationChange: (page: number) => void;
  totalRowCount?: number;
  responseTotals?: Record<string, any>;
  fieldStyle?: Record<string, FieldStyleType> | undefined;
  formattingRuleId?: string | undefined;
  clickInteractions?: ChartClickInteraction[];
  currentIndex?: number;
  selected?: any[];
  isEditingWidget?: boolean;
  sx?: SxProps;
  resizable?: boolean;
}

export default function PivotTable({
  widgetData,
  groupOthers,
  offset,
  rowSize,
  showTotals,
  showColTotals,
  data,
  headCells,
  onSort,
  isLoading,
  rowLimit,
  onPaginationChange,
  totalRowCount,
  responseTotals,
  fieldStyle,
  formattingRuleId,
  clickInteractions,
  currentIndex,
  selected = emptyArray,
  isEditingWidget = false,
  resizable = true,
  sx = {},
}: Props) {
  const theme = useTheme();

  const {
    headCellsWithSelectedDimAndMetrics,
    multiLevelGroupedHeadCells,
    parsedData,
    totalsRow,
    totalsCells,
    emptyCellStyle = emptyObject,
  } = useGetPivotTableProps({
    projName: widgetData.projectionId.name,
    dims: widgetData.select.dims,
    metrics: widgetData.select.metrics,
    showColTotals,
    sortingCol: widgetData.sortingCol,
    sortingRow: widgetData.sorting,
    data,
    headCells,
    responseTotals,
    fieldStyle,
    formattingRuleId,
    clickInteractions,
    currentIndex,
    selected,
    isEditingWidget,
  });

  const onSortPivot = (fieldName: string, dir: SortType | null, event, options: Record<string, any> | undefined) => {
    const newSorting = options
      ? [
          {
            name: fieldName,
            dir: dir === 'asc' ? SortDir.ASC : SortDir.DESC,
            path: { path: getSortingPath(['pivot', 'buckets', options.group, fieldName]) },
          },
        ]
      : [{ name: fieldName, dir: dir === 'asc' ? SortDir.ASC : SortDir.DESC }];
    onSort(newSorting);
  };

  return (
    <Box
      className="draggableCancel"
      sx={{
        ...flex.center,
        flex: 1,
        overflow: 'auto',
        p: 2,
        ...(!!responseTotals &&
          showTotals && {
            // Apply style to the totals row
            '.totals-row': {
              zIndex: 1,
            },
            // Apply style to the cells in totals column in the row before the totals row
            'tr:has(+ .totals-row) > .MuiTableCell-root:not(.MuiTableCell-head):not(.totals-row)': {
              borderBottom: 0,
            },
          }),
        ...sx,
      }}>
      <AvTable
        shouldDisplayNull
        headCells={headCellsWithSelectedDimAndMetrics}
        groupedHeadCells={{
          isMultiLevel: false,
          cells: multiLevelGroupedHeadCells,
          innerCols: widgetData.select.metrics.length,
          emptyCellStyle: { position: 'sticky', left: 0, ...emptyCellStyle },
        }}
        loading={isLoading}
        onSort={onSortPivot}
        getSortingDir={getSortingDir}
        size={rowSize}
        rows={parsedData}
        pagination={!groupOthers}
        resizable={resizable}
        rowLimit={rowLimit}
        paginationProps={{
          onPageChange: page => onPaginationChange(page),
          totalRows: totalRowCount,
          pageNum: (offset || 0) / rowLimit,
        }}
        totalProps={
          responseTotals && showTotals
            ? {
                label: 'Totals',
                row: totalsRow,
                cells: totalsCells,
                // Applies style to the "Totals" cell in the row dim column
                sx: {
                  position: 'sticky',
                  left: 0,
                  borderRight: `1px solid var(--color-border-base-secondary)`,
                  borderTop: `1px solid var(--color-border-base-secondary)`,
                  backgroundColor: 'var(--color-surface-base-secondary)',
                },
              }
            : {}
        }
        displayFreeSpaceCell={false}
        sx={{
          height: '100%',
          // Applies style to the column headcells
          [COLUMNS_HEADERS_STYLE_CLASS]: { zIndex: 2 },
          [EXISTING_COULMN_HEADERS_OVERRIDE_STYLE_CLASS]: {
            color: theme.palette.colors.neutrals[700],
            textTransform: 'none',
          },
        }}
        {...commonTableProps}
        hasHeaderFilters={false}
      />
    </Box>
  );
}
